import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import StatusChip from "../components/StatusChip";
import RescheduleDialog from "../components/RescheduleDialog";
import { useAuth } from "../context/AuthContext";

const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");

export default function Trips() {
  const nav = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [tab, setTab] = useState("active");
  const [resched, setResched] = useState(null);
  const { user } = useAuth();

  const load = () => {
    api.get("/bookings", { params: { status: "pending_sm_approval,blocked,approved,slot_missed" } }).then((r) => setBlocks(r.data.filter((b) => !b.trip_started)));
    api.get("/trips").then((r) => setTrips(r.data));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const release = async (b) => {
    if (!window.confirm(`Cancel this test drive for ${b.customer_name}? SM and TDC will be alerted.`)) return;
    try { await api.post(`/bookings/${b.id}/release`); toast.success("Test drive cancelled — SM and TDC alerted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const isReservation = (b) => b.advance || b.type === "home" || b.type === "connected";
  const canStart = ["tdc", "sm", "admin"].includes(user.role);
  const owns = (b) => user.role !== "sales" || b.kec_roster_id === user.id || b.created_by === user.id;
  const typeLabel = (b) => b.type === "connected" ? `Connected Drive — ${(b.customers || []).length} customers` : b.type === "home" ? "Home TD" : b.type === "utility" ? `Utility · ${(b.utility_purpose || "").replace(/_/g, " ")}` : b.type === "transfer" ? `Drives on transfer · ${b.td_count || 0} given` : "Showroom TD";

  const active = trips.filter((t) => ["started", "gate_out", "gate_in"].includes(t.status));
  const closed = trips.filter((t) => t.status === "closed");

  return (
    <div className="px-4 py-5 lg:px-8 max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Test Drives</h1>

      <div className="mt-4 flex rounded-xl bg-slate-100 p-1">
        {[["active", `Active (${blocks.length + active.length})`], ["history", "History"]].map(([t, l]) => (
          <button key={t} data-testid={`trips-tab-${t}`} onClick={() => setTab(t)}
            className={`flex-1 rounded-lg py-2 text-sm font-bold ${tab === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "active" && (
        <div className="mt-4 space-y-3">
          {blocks.length === 0 && active.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No active blocks or trips</div>}
          {blocks.map((b) => (
            <div key={b.id} data-testid={`block-card-${b.reg_no}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-bold text-[#0E2240]">{b.reg_no} · {b.model}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.kec_name} → {b.customer_name} · <span data-testid={`type-label-${b.reg_no}`}>{typeLabel(b)}</span>{b.enquiry_id && ` · ENQ ${b.enquiry_id}`}</div>
                  {b.type === "connected" && (b.customers || []).length > 0 && <div className="mt-0.5 text-[11px] text-slate-500">{b.customers.map((c) => `${c.customer_name} (${c.kec_name})`).join(" · ")}</div>}
                  {isReservation(b) && b.status !== "blocked"
                    ? <div data-testid={`reservation-slot-${b.reg_no}`} className="text-[11px] font-semibold text-sky-700 mt-0.5">Reserved slot {dt(b.slot_start)}</div>
                    : <div className="text-[11px] text-slate-400 mt-0.5">Block until {dt(b.block_until)}</div>}
                  {b.status === "slot_missed" && <div className="mt-1 rounded-lg bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700">{b.slot_missed_reason}. Pick a new slot or ask the TDC.</div>}
                </div>
                <StatusChip status={b.status} />
              </div>
              <div className="mt-3 flex gap-2">
                {b.status === "blocked" ? (
                  canStart ? (
                    <button data-testid={`start-trip-btn-${b.reg_no}`} onClick={() => nav(`/trip/new?booking=${b.id}`)}
                      className="flex-1 rounded-xl bg-[#BB162B] py-2.5 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150">Start Trip</button>
                  ) : (
                    <div data-testid={`awaiting-tdc-${b.reg_no}`} className="flex-1 rounded-xl bg-slate-100 border border-slate-200 py-2.5 text-center text-xs font-bold text-slate-600">Ready — TDC starts the trip at the branch</div>
                  )
                ) : b.status === "pending_sm_approval" ? (
                  <div className="flex-1 rounded-xl bg-amber-50 border border-amber-200 py-2.5 text-center text-xs font-bold text-amber-700">Awaiting SM approval — trip locked</div>
                ) : b.status === "approved" ? (
                  <div className="flex-1 rounded-xl bg-sky-50 border border-sky-200 py-2.5 text-center text-xs font-bold text-sky-700">Reserved — car is held from the slot time</div>
                ) : null}
                {isReservation(b) && owns(b) && (
                  <button data-testid={`reschedule-btn-${b.reg_no}`} onClick={() => setResched(b)}
                    className="rounded-xl border border-[#0E2240] px-3 py-2.5 text-sm font-semibold text-[#0E2240]">Reschedule</button>
                )}
                {owns(b) && <button data-testid={`release-block-btn-${b.reg_no}`} onClick={() => release(b)}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>}
              </div>
            </div>
          ))}
          {active.map((t) => (
            <button key={t.id} data-testid={`active-trip-card-${t.reg_no}`} onClick={() => nav(`/trip/${t.id}`)}
              className="card-hover w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-bold text-[#0E2240]">{t.reg_no} · {t.model}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.kec_name} → {t.customer_name} · <span data-testid={`active-type-label-${t.reg_no}`}>{typeLabel(t)}</span></div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Opening {t.opening_km} km · started {dt(t.created_at)}</div>
                </div>
                <StatusChip status={t.status} />
              </div>
            </button>
          ))}
        </div>
      )}

      {resched && <RescheduleDialog booking={resched} onClose={() => setResched(null)} onDone={load} />}
      {tab === "history" && (
        <div className="mt-4 space-y-2">
          {closed.slice(0, 40).map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm flex justify-between items-center">
              <div>
                <div className="font-semibold text-[#0E2240] text-[13px]">{t.reg_no} · {t.kec_name} · {t.km_driven ?? ((t.closing_km || 0) - t.opening_km)} km</div>
                <div className="text-[11px] text-slate-400">{t.customer_name} · {t.type} · {dt(t.closed_ts)}</div>
              </div>
              <div className="flex gap-1">
                {t.deviation_flag && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-700">DEVIATION</span>}
                {(t.gate_pass?.odo_mismatch_out || t.gate_pass?.odo_mismatch_in) && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700">ODO MISMATCH</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
